import { Box, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import WheelzTextInput from "../../common/inputs/WheelzTextInput";
import STRINGS from "../../../assets/strings/Strings";
import WheelzButton, {
  WHEELZ_BUTTON_TYPES,
} from "../../common/buttons/WheelzButton";
import APP_CONSTANTS from "../../../constants/AppConstants";
import notify from "../../../funcs/ToastFuncs";
import useNetworkRequest from "../../../hooks/NetworkRequest";
import { ENDPOINTS, urlWithPrefix } from "../../../configs/APIConfig";
import OTPInput from "react-otp-input";
import COLORS from "../../../constants/Colors";
import LS_CONSTANTS from "../../../constants/LSConstants";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";

const LOGIN_STATES = {
  ACCEPT_NUMBER: "ACCEPT_NUMBER",
  ACCEPT_OTP: "ACCEPT_OTP",
};

function EmailLoginForm() {
  const [loginState, setLoginState] = useState(LOGIN_STATES.ACCEPT_NUMBER);
  const { fetchData } = useNetworkRequest();
  const abortControllerRef = useRef(null);
  const [inputEmail, setinputEmail] = useState("");
  const [inputEmailInvalid, setinputEmailInvalid] = useState(false);
  const [inputEmailErrMsg, setinputEmailErrMsg] = useState("");
  const [buttonLoading, setbuttonLoading] = useState();
  const [otp, setOtp] = useState("");
  const [disableGetOtpBtn, setdisableGetOtpBtn] = useState(true);
  const [disableValidateOtpBtn, setdisableValidateOtpBtn] = useState(true);
  const [otpRequestAttempts, setotpRequestAttempts] = useState(0);
  const [otpValidationAttempts, setotpValidationAttempts] = useState(0);
  const [requestOtpResp, setRequestOtpResp] = useState({});
  const [countdown, setCountdown] = useState(
    APP_CONSTANTS.OTP_RESEND_COUNTDOWN_TIME
  );
  const [isCounting, setIsCounting] = useState(false);
  const [redirectPath, setredirectPath] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const redirectInfo = location.state && location.state.redirectInfo;
  useEffect(() => {
    if (redirectInfo) {
      setredirectPath(redirectInfo);
    }
  }, [redirectInfo]);

  useEffect(() => {
    if (validateEmail(true)) {
      setdisableGetOtpBtn(false);
    } else {
      setdisableGetOtpBtn(true);
    }
    // eslint-disable-next-line
  }, [inputEmail]);

  useEffect(() => {
    if (otp.length === APP_CONSTANTS.LOGIN_OTP_LENGTH) {
      setdisableValidateOtpBtn(false);
    } else {
      setdisableValidateOtpBtn(true);
    }
  }, [otp]);

  const validateEmail = (noErr = false) => {
    // Regex pattern for email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inputEmail)) {
      if (!noErr) {
        setinputEmailInvalid(true);
        setinputEmailErrMsg(STRINGS.LOGIN_SCREEN.INVALID_EMAIL);
      }
      return false;
    } else {
      if (!noErr) resetEmailInputErr();
      return true;
    }
  };

  const resetEmailInputErr = () => {
    setinputEmailErrMsg("");
    setinputEmailInvalid(false);
  };

  const handleEmailInputChange = (event) => {
    resetEmailInputErr();
    let inputValue = event.target.value;
    inputValue = inputValue.replace(" ", "");
    if (inputValue.length > APP_CONSTANTS.MAX_EMAIL_LENGTH) {
      inputValue = inputValue.slice(0, APP_CONSTANTS.MAX_EMAIL_LENGTH);
    }
    setinputEmail(inputValue);
  };

  const handleLogin = () => {
    if (validateEmail()) {
      reqOTP();
    }
  };

  const handleOTPChange = (newValue) => {
    setOtp(newValue);
  };

  const handleOTPKeyDown = (event) => {
    // Check if the key pressed is 'Enter'
    if (!disableValidateOtpBtn && event.key === "Enter") {
      // Call the validateOtp function
      validateOTP();
    }
  };

  const resetInputs = () => {
    setLoginState(LOGIN_STATES.ACCEPT_NUMBER);
    setotpRequestAttempts(0);
    setotpValidationAttempts(0);
    setinputEmail("");
    setOtp("");
  };

  useEffect(() => {
    let timer;

    if (isCounting && countdown > 0) {
      timer = setTimeout(() => {
        if (countdown === 1) {
          setIsCounting(false);
        }
        setCountdown(countdown - 1);
      }, 1000);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [countdown, isCounting]);

  const startCountdown = () => {
    // Reset countdown
    setCountdown(APP_CONSTANTS.OTP_RESEND_COUNTDOWN_TIME);
    // Start the countdown
    setIsCounting(true);
  };

  const resendOtp = () => {
    setOtp("");
    reqOTP();
  };

  const reqOTP = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setbuttonLoading(true);
    setRequestOtpResp({});
    setOtp("");
    setotpRequestAttempts(otpRequestAttempts + 1);
    if (otpRequestAttempts >= APP_CONSTANTS.OTP_REQUEST_LIMIT) {
      notify(STRINGS.LOGIN_SCREEN.EXCEEDED_OTP_ATTEMPTS, "error");
      resetInputs();
      setbuttonLoading(false);
      return;
    }
    const requestOtpBody = {
      otpauth: {
        did: "device_id",
        email: inputEmail,
      },
    };
    try {
      const res = await fetchData({
        url: urlWithPrefix(ENDPOINTS.LOGIN.REQUEST_EMAIL_OTP.url),
        method: ENDPOINTS.LOGIN.REQUEST_EMAIL_OTP.method,
        body: requestOtpBody,
        authRequired: false,
        timeout: 20000, // 20 Sec timeout
        signal: abortController.signal,
      });
      const respData = res.data;
      if (res.status === 200) {
        setRequestOtpResp(respData);
        setLoginState(LOGIN_STATES.ACCEPT_OTP);
        startCountdown();
      } else {
        notify(STRINGS.LOGIN_SCREEN.OTP_REQ_FAILED, "error");
        setLoginState(LOGIN_STATES.ACCEPT_NUMBER);
      }
    } catch (error) {
      console.error("Error - reqOTP", error);

      const errorMessages = {
        "user-not-registered": "User not registered.",
        "email-invalid": "Invalid email address.",
        "usr-inactive": "Access blocked.",
      };

      const defError = "Oops! Failed to request OTP";

      const errorMessage = error?.response?.data?.error?.code
        ? errorMessages[error.response.data.error.code] || defError
        : defError;

      notify(errorMessage, "error");
    }
    setbuttonLoading(false);
  };

  const setSessionData = () => {
    const sessionData = {};
    sessionData.loggedInTime = Date.now();
    localStorage.setItem(
      LS_CONSTANTS.LS_USER_SESSION_DATA,
      JSON.stringify(sessionData)
    );
  };

  const redirectToScreen = async () => {
    setbuttonLoading(true);
    const id = toast.loading("Please wait...");
    setTimeout(() => {
      if (redirectPath) {
        navigate(redirectPath.replace(APP_CONSTANTS.BASENAME, ""));
      } else {
        navigate("/dashboard");
      }
      toast.update(id, {
        render: "Logged in!",
        type: "success",
        isLoading: false,
      });
      setTimeout(() => {
        toast.dismiss();
        setbuttonLoading(false);
      }, 1000);
    }, 2000);
  };

  const getTransporterProf = async (transpId) => {
    let transpData = {};
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    try {
      const resp = await fetchData({
        url: urlWithPrefix(
          ENDPOINTS.LOGIN.GET_TRANSPORTER_PROFILE.url.replace(
            "<transporter_id>",
            transpId
          )
        ),
        method: ENDPOINTS.LOGIN.GET_TRANSPORTER_PROFILE.method,
        authRequired: true,
        timeout: 20000, // 20 Sec timeout
        signal: abortController.signal,
      });
      if (resp.status === 200) {
        transpData = resp.data;
      }
    } catch (error) {
      console.error("Transporter profile fetch failure", error);
    }
    return transpData;
  };

  const validateOTP = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    setbuttonLoading(true);
    const validateOtpBody = {
      otpauth: {
        mdigest: requestOtpResp.otpauth.mdigest,
        code: otp,
      },
    };
    try {
      const res = await fetchData({
        url: urlWithPrefix(ENDPOINTS.LOGIN.VALIDATE_OTP.url),
        method: ENDPOINTS.LOGIN.VALIDATE_OTP.method,
        body: validateOtpBody,
        authRequired: false,
        timeout: 20000, // 20 Sec timeout
        signal: abortController.signal,
      });
      const respData = res.data;
      if (res.status === 200) {
        localStorage.setItem(
          LS_CONSTANTS.LS_USER_DATA,
          JSON.stringify(respData)
        );
        const traspData = await getTransporterProf(respData.subject.org_id);
        localStorage.setItem(
          LS_CONSTANTS.LS_TRANS_DATA,
          JSON.stringify(traspData)
        );
        setSessionData();
        redirectToScreen();
      } else {
        throw new Error("Failed to validate OTP");
      }
    } catch (error) {
      setbuttonLoading(false);
      const statusCode = error.response.status;
      if (statusCode === 400) {
        const errorCode = error.response.data.error.code;
        if (errorCode === "otp-invalid") {
          setotpValidationAttempts(otpValidationAttempts + 1);
          if (
            otpValidationAttempts < APP_CONSTANTS.OTP_VALIDATION_ATTEMPT_LIMIT
          ) {
            notify(STRINGS.LOGIN_SCREEN.INVALID_OTP, "error", false);
            setOtp("");
          } else {
            notify(STRINGS.LOGIN_SCREEN.EXCEEDED_ATTEMPTS, "error");
            resetInputs();
          }
        } else {
          notify("Oops! " + errorCode, "error");
        }
      } else {
        notify("Oops! Error fetching data", "error");
        console.error("Error - validateOtp", error);
      }
    }
  };

  const OtpSection = (
    <Box sx={{ mt: "30px" }}>
      <Typography
        color={COLORS.PRIMARY}
        sx={{
          fontWeight: 500,

          fontSize: "15px",
        }}
      >
        OTP
      </Typography>
      <Typography
        sx={{
          fontWeight: 400,

          fontSize: "14px",
          opacity: "0.5",
          mt: "8px",
        }}
      >
        We've sent a OTP to above email, please enter it below.
      </Typography>
      <Box sx={{ mt: "10px" }}>
        <div onKeyDown={handleOTPKeyDown}>
          <OTPInput
            value={otp}
            onChange={handleOTPChange}
            numInputs={APP_CONSTANTS.LOGIN_OTP_LENGTH}
            renderSeparator={<></>}
            inputType="tel"
            renderInput={(props) => (
              <input
                {...props}
                style={{
                  fontFamily: "inherit", // Change to your desired font
                  fontSize: "16px", // Change to your desired font size
                  fontWeight: "600",
                  padding: "10px",
                  width: `calc(${
                    250 / APP_CONSTANTS.LOGIN_OTP_LENGTH
                  }px - 18px)`,
                  border: "2px solid var(--otp-border)",
                  background: "var(--input-field-active)",
                  borderRadius: "4px",
                  margin: "0 5px",
                  textAlign: "center",
                }}
              />
            )}
            onKeyDown={handleOTPKeyDown}
          />
        </div>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          mt: "8px",
        }}
      >
        <Typography
          sx={{
            fontWeight: 400,

            fontSize: "14px",
            opacity: "0.5",
            mr: "8px",
            textWrap: "nowrap",
          }}
        >
          Didn’t receive code?
        </Typography>
        {isCounting ? (
          <Typography
            sx={{
              fontWeight: 600,

              fontSize: "14px",
              opacity: "0.5",
              textWrap: "nowrap",
            }}
          >
            Resend in {countdown}s
          </Typography>
        ) : (
          <Typography
            onClick={resendOtp}
            sx={{
              fontWeight: 600,

              fontSize: "14px",
              color: COLORS.PRIMARY,
              textWrap: "nowrap",
              cursor: "pointer",
            }}
          >
            Resend
          </Typography>
        )}
        <Typography
          sx={{
            fontWeight: 400,

            fontSize: "14px",
            opacity: "0.5",
            textWrap: "nowrap",
            margin: "0px 8px",
          }}
        >
          or
        </Typography>
        <Typography
          onClick={resetInputs}
          sx={{
            fontWeight: 600,

            fontSize: "14px",
            color: COLORS.PRIMARY,
            textWrap: "nowrap",
            cursor: "pointer",
          }}
        >
          Change email.
        </Typography>
      </Box>
    </Box>
  );

  const EmailAdornment = () => (
    <Box
      sx={{
        width: "50px",
        height: "40px",
        mr: "10px",
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        background: COLORS.PRIMARY,
      }}
    >
      <EmailRoundedIcon
        sx={{
          color: "white",
          fontSize: "1.5rem",
        }}
      />
    </Box>
  );

  return (
    <Box
      sx={{
        width: "350px",
      }}
    >
      <Typography
        fontSize={"1.5rem"}
        fontWeight={800}
        sx={{
          userSelect: "none",
        }}
      >
        Authentis
      </Typography>
      <Box
        sx={{
          height: "5px",
          width: "112px",
          mb: "50px",
          background: COLORS.INDIAN_FLAG_GRAD,
        }}
      />
      <Typography fontSize={"1.2rem"} fontWeight={500}>
        Login to your account
      </Typography>
      <Box
        sx={{
          width: "100%",
          mt: "20px",
        }}
      >
        <WheelzTextInput
          label={"Email"}
          placeholder={"Enter email id here"}
          primaryColorLabel
          value={inputEmail}
          onChange={handleEmailInputChange}
          error={inputEmailInvalid}
          customStartAdornment={inputEmail.length !== 0 && <EmailAdornment />}
          helperText={inputEmailInvalid && inputEmailErrMsg}
          readOnly={loginState === LOGIN_STATES.ACCEPT_OTP}
          onEnterFunc={
            loginState === LOGIN_STATES.ACCEPT_NUMBER ? handleLogin : null
          }
        />
      </Box>
      {loginState === LOGIN_STATES.ACCEPT_OTP && OtpSection}
      <Box sx={{ mt: "30px" }}>
        <WheelzButton
          buttonText={
            loginState === LOGIN_STATES.ACCEPT_NUMBER ? "Get OTP" : "Verify OTP"
          }
          width={"100%"}
          deCapitalizeText
          buttonType={WHEELZ_BUTTON_TYPES.PRIMARY}
          onClick={
            buttonLoading
              ? () => {}
              : loginState === LOGIN_STATES.ACCEPT_NUMBER
              ? handleLogin
              : validateOTP
          }
          disabled={
            loginState === LOGIN_STATES.ACCEPT_NUMBER
              ? disableGetOtpBtn
              : disableValidateOtpBtn
          }
          loading={buttonLoading}
        />
      </Box>
    </Box>
  );
}

export default EmailLoginForm;
